import { defaults } from 'ol/control/defaults'
import { intersects } from 'ol/extent'
import Feature from 'ol/Feature'
import MultiLineString from 'ol/geom/MultiLineString'
import { defaults as iDefaults } from 'ol/interaction/defaults';
import Link from 'ol/interaction/Link';
import LayerGroup from 'ol/layer/Group'
import TileLayer from 'ol/layer/Tile.js';
import VectorLayer from 'ol/layer/Vector'
import Map from 'ol/Map'
import { get as getProjection } from 'ol/proj';
import OSM from 'ol/source/OSM.js';
import VectorSource from 'ol/source/Vector'
import Style from 'ol/style/Style'
import View from 'ol/View'

import { BaseMapControl } from '../controls/basemapSelector'
import { ZoomHomeControl } from '../controls/zoomHome'
import { basemap, createWMS, dragAndDropInteraction } from './layers'
import {
  drawnVectorLayer, removableBorders, updatedVectorLayer, vectorLayer
} from './layers'
import { debug, maxEditResolution, minZoom, maxZoom } from '../storage'
import { styleFunctionFromOpacity } from '../styles'

export let map;
export const setMap = (m) => map = m;

export let mapsToBeSteered = [];
export const setMapsToBeSteered = (mapArray) => mapsToBeSteered = mapArray;

export const link = new Link({ params: ['x', 'y', 'z'] });
// get initial x from url (track will then trigger on history change, not moveend)
// const ix = link.track('x', newValue => console.log(getUtm()));
// console.log(ix);
const p = new URLSearchParams(window.location.search);
const urlPosition = p.has('x') && p.has('y') && p.has('z') && p.has('utm');
const initialUtm = urlPosition ? Number(p.get('utm')) : 35;

// const ibm = link.track('map', newValue => console.log(newValue));
const ibm = p.get('map');
const urlBasemap = basemap[ibm];
if (!urlBasemap) link.update('map', 'graatoner');
const bm = urlBasemap || basemap.graatoner;

export let initialPositionSource = urlPosition ? 'url' : 'default';
export const setInitialPositionSource = s => initialPositionSource = s;

export const getEpsg = () =>
  map ? map.getView().getProjection().getCode() : `EPSG:258${initialUtm}`;

export const getEpsgCode = () => Number(getEpsg().split(':')[1]);

export const getUtm = () => Number(getEpsg().slice(-2));

export const createMap = () => map = new Map({
  target: 'map',
  // groupPosition: 1,
  // maxTilesLoading: 32,
  layers: [
    new TileLayer({ id: 'OSM', source: new OSM(), visible: false }),
    bm[getEpsg()],
    new LayerGroup({ id: 'wmsGroup', layers: [], zIndex: 2 }),
    // gpsLayer, marker, boundary,
    vectorLayer, removableBorders, drawnVectorLayer, updatedVectorLayer
  ],
  controls: defaults({
    attribution: false,
    zoomOptions: {
      duration: 1100,
      zoomInTipLabel: 'Zoom inn',
      zoomOutTipLabel: 'Zoom ut',
    }
  }).extend([
    // new app.LayerButtonControl(), // moved to rein_ar5.js
    // new LegendControl(),
    // new AreaControl(),
    // new LengthControl(),
    new BaseMapControl(),
    new ZoomHomeControl({ id: 'zoomHome' }),
    dragAndDropInteraction
  ]),
  interactions: iDefaults().extend([link]),
  view: new View({
    center: urlPosition ? [+p.get('x'), +p.get('y')] : [572016, 7201790],
    constrainOnlyCenter: true,
    extent: getProjection(`EPSG:258${initialUtm}`).getExtent(),
    maxZoom: maxZoom,
    minZoom: minZoom,
    zoom: urlPosition ? Number(p.get('z')) : minZoom,
    projection: getEpsg()
  })
});

const printMapLayers = () => {
  map.getLayers().forEach( ml => console.log(ml.get('id')) );
};

export const getLayerFromMapById = (map, id) => {
  const layers = map.getLayers();
  const nl = layers.getLength();
  for (let i = 0; i < nl; i++) {
    const l = layers.item(i);
    if (l.get('id') === id) return l;
  }
  if (id === 'wmsGroup') return null; // avoid loop in case wmsGroup is not found
  // If it has not been found yet, it could be a wms inside wmsGroup:
  const wmses = getLayerFromMapById(map, 'wmsGroup').getLayers();
  const nw = wmses.getLength();
  for (let i = 0; i < nw; i++) {
    const w = wmses.item(i);
    if (w.get('id') === id) return w;
  }
};
export const getLayerById = (id) => getLayerFromMapById(map, id);

export const getLayerIndex = (layerId) => {
  const layers = map.getLayers();
  const nl = layers.getLength();
  for (let i = 0; i < nl; i++) {
    const l = layers.item(i);
    if (l.get('id') === layerId) return i;
  }
};

const getGroupLayerIndex = (m, groupLayerId) => {
  const groupLayers = getLayerFromMapById(m, 'wmsGroup').getLayers();
  const ngl = groupLayers.getLength();
  for (let i = 0; i < ngl; i++) {
    const gl = groupLayers.item(i);
    if (gl.get('id') === groupLayerId) return i;
  }
};

export const addGroupLayer = (m, gl) => {
  const gIndex = m.get('groupPosition'); // position of WMSes
  const gLayers = m.getLayers().item(gIndex)?.getLayers();
  if (gl.get('id') === 'ortoWMS') gLayers?.insertAt(0, gl);
  else gLayers?.push(gl);
};

const removeGroupLayer = (m, gl) => {
  const i = getGroupLayerIndex(m, gl.get('id'));
  getLayerFromMapById(m, 'wmsGroup').getLayers().removeAt(i);
};

export const removeLayerById = name => map.removeLayer(getLayerById(name));

export const changeLayerOpacity = (e) => {
  const [id, wmsName, opacity] = [e.target.id, e.target.name, e.target.value];
  const wms = getLayerById(wmsName);
  wms.setOpacity(opacity / 100);
  // if (['arealtypeWMS', 'ar5_13WMS'].indexOf(wmsName) > -1) {
  //   if (!document.getElementById(wmsName).checked) return;
  //   ar5orthoWMS.image.dynamicProjection.setOpacity((100 - opacity) / 100);
  // }
  // Update mirror slider:
  document.getElementById(id + 'mirror').value = opacity;
};

const getFeatureIndex = (layer, f) => {
  const ff = layer.getSource().getFeatures();
  const l = ff.length;
  const fid = f.get('id');
  for (let i = 0; i < l; i++) if (ff[i].get('id') === fid) return i;
  return -1;
};

export const flyToExtent = (e) => {
  const view = map.getView();
  const z = view.getZoom();
  const fit = () => view.fit(e, { duration: 2000, padding: [50, 50, 50, 50] });
  const visibleDestination = intersects(view.calculateExtent(), e);
  if (z < 8 || visibleDestination) fit(); else view.animate({ zoom: 7 }, fit);
};

export const toSingleFeature = (features) => {
  const num = features.length;
  if (debug < 14) console.log('From', num, 'features to single feature.');
  if (num < 1) return new Feature([]);
  else if (num === 1) return features[0];
  else {
    const linestrings = [];
    features.forEach(f => linestrings.push(f.getGeometry()));
    const multilinestring = new MultiLineString(linestrings);
    // console.log('multilinestring', multilinestring.getLineStrings().length);
    return new Feature(multilinestring);
  };
};

export const getWMS = (id, m = map) => { // m is map as default
  return getLayerFromMapById(m, id) ?? createWMS(id).tile;
};

export const vectorIntersectsWms = (m) => {
  // const v = m.getLayerById(id_after);
  const v = m.getLayers().item(2);
  const f = v.getSource().getFeatures()[0];
  // console.log(f.getGeometry().getType());
  // const c = f.getGeometry().getPolygons()[0].getCoordinates()[0];
  const c = f.getGeometry().getCoordinates()[0];
  const l = c.length;

  const wms = getLayerFromMapById(m, 'utenforWMS');
  if (wms && wms.getVisible()) {
    for (let i = 0; i < l; i++) {
      const pixel = m.getPixelFromCoordinate(c[i]);
      const rgba = wms.getData(pixel);
      // console.log(c[i], pixel, rgba);
      if (rgba && rgba.length > 3 && rgba[3] > 0) return true;
    }
  }
  return false;
};

export const setLimits = (id, min, max) => {
  const existing = getLayerById(id);
  const wms = getWMS(id);
  wms.setMinResolution(min);
  wms.setMaxResolution(max);
  if (!existing) {
    wms.setVisible(false);
    addGroupLayer(map, wms);
  }
};

export const tuneOpacity = (opacity) => {
  // g.selectAll('path').style('fill-opacity', opacity / 100);
  // or feature.style('fill-opacity', opacity / 100);
  // document.getElementById('postgisOpacityValue').innerHTML = opacity;
  // vectorLayer.setOpacity(opacity / 100); // hide symbols too
  vectorLayer.setStyle(styleFunctionFromOpacity.get(opacity / 100));
};

